import React, {useMemo, useState} from 'react';
import _ from 'lodash';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {Box, Table, TableBody, TableContainer} from '@mui/material';
import {useParamReportPeriodOrDefault} from '../../../../util/useParamReportPeriodOrDefault';
import {APP_HEADER_HEIGHT} from '../../../../App';
import {
  useGetIndirectCashflowWorkbookQuery,
  useGetBalanceSheetSummaryQuery,
  useGetCtaSummaryQuery,
  useGetReportsListQuery,
} from '../../../../data/api';
import {BalanceSheetHead} from './BalanceSheetHead';
import {WorkbookPageHeader} from '../WorkbookPageHeader';
import {CashflowRow} from './CashflowRow';
import LoadingButton from '@mui/lab/LoadingButton';
import {DownloadIcon} from 'lucide-react';
import {useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import SinglePeriodSelect from '../../../../components/ReportPeriodSelect/SinglePeriodSelect';
import {getMinPeriodDate} from '../../../../util/date';
import {setSelectedPeriod} from '../../../../data/periods';
import {TotalsRow} from './TotalsRow';
import {LoadingWorkbookTable} from './LoadingWorkbookTable';
import Decimal from 'decimal.js-light';
import {ExchangeRateModal} from './ExchangeRateModal';
import NoData from '../../../../components/NoData';
import {AociModal} from './AociModal';
import {AccumulatedDeficitModal} from './AccumulatedDeficitModal';
import {triggerDownload} from '../../../../util';
import * as api from '../../../../util/api';

const periodSelectStyles = {
  border: `1px solid #DCE2E0`,
  borderRadius: '8px',
  minWidth: 'min-content',
  width: 'min-content',
  fontSize: '12px',
  height: 'min-content',
  marginLeft: 2,
};

const activeReportType = 'INDIRECT_CASHFLOW_ONES';

function IndirectCashflowPage() {
  const {statementsDashboard: statementsEnabled} = useFlags();
  const period = useParamReportPeriodOrDefault();
  const dispatch = useDispatch();
  const periods = useSelector((state) => state.periods);
  const {orgConfig, currentOrg} = useSelector((state) => state.session);
  const [, setSearchParams] = useSearchParams();
  const [isDownloading, setIsDownloading] = useState(false);

  function handlePeriodChange(newPeriod) {
    dispatch(setSelectedPeriod({period: newPeriod}));
    setSearchParams({period: newPeriod});
  }

  const {data: reportsListData, isFetching: cashflowSummaryFetching} =
    useGetReportsListQuery({period}, {skip: !period});

  const {data: cashflowWorkbookData, isFetching: cashflowWorkbookFetching} =
    useGetIndirectCashflowWorkbookQuery({period}, {skip: !period});
  const {
    data: balanceSheetSummaryData,
    isFetching: balanceSheetSummaryFetching,
  } = useGetBalanceSheetSummaryQuery({period}, {skip: !period});
  const {data: ctaSummaryData, isFetching: ctaSummaryFetching} =
    useGetCtaSummaryQuery({period}, {skip: false});

  const isFetching =
    cashflowSummaryFetching ||
    cashflowWorkbookFetching ||
    ctaSummaryFetching ||
    balanceSheetSummaryFetching;

  const reportConfig = useMemo(() => {
    const rows =
      reportsListData?.reports[0]?.data.find(
        ({reportType}) => reportType === activeReportType
      )?.reportConfig || null;
    if (!rows) {
      return [];
    }

    return rows;
  }, [reportsListData]);

  const [differenceData, balanceSheetPliIds, totalsRow] = useMemo(() => {
    const diff = [];
    const plis = [];
    const totals = [];

    if (!balanceSheetSummaryData || !ctaSummaryData || !cashflowWorkbookData) {
      return [diff, plis, totals];
    }

    const keys = Object.keys(balanceSheetSummaryData);

    const bsLeft = balanceSheetSummaryData[keys[0]];
    const bsRight = balanceSheetSummaryData[keys[1]];

    const longestBSData = bsLeft.length > bsRight.length ? bsLeft : bsRight;

    const cashflowLookup = cashflowWorkbookData.reduce((map, cashflow) => {
      if (!map[cashflow.pli_id]) {
        map[cashflow.pli_id] = {
          amount: 0,
          ruleName: cashflow.rule_name,
        };
      }

      map[cashflow.pli_id].amount += cashflow.amount;
      return map;
    }, {});

    for (let i = 0; i < longestBSData.length; i++) {
      const leftAmount = bsLeft[i]?.amount || 0;
      const rightAmount = bsRight[i]?.amount || 0;

      diff[i] = leftAmount - rightAmount;
      plis[i] = longestBSData[i].pli_id;

      let cashflowTotal = 0;
      if (cashflowLookup[plis[i]]) {
        cashflowTotal = cashflowLookup[plis[i]].amount;
      }

      totals[i] = diff[i] + cashflowTotal;
    }

    return [diff, plis, totals];
  }, [balanceSheetSummaryData, cashflowWorkbookData, ctaSummaryData]);

  const renderRows = (rows) => {
    return rows.map((row) => {
      if (row.config.hideLine && row.children.length > 0) {
        return renderRows(row.children);
      }

      if (row.config.isHeaderLine) {
        return (
          <React.Fragment key={row._locatorId}>
            {renderRow(row)}
            {renderRows(row.children)}
          </React.Fragment>
        );
      }

      if (!row.config.isHeaderLine) {
        return (
          <React.Fragment key={row._locatorId}>
            {renderRows(row.children)}
            {renderRow(row)}
          </React.Fragment>
        );
      }

      return null;
    });
  };

  const [activeExchangeData, setActiveExchangeData] = useState(null);
  function onExchangeRateClick(pliId) {
    const data = ctaSummaryData.find((item) => item.pli_id === pliId);
    if (data) {
      setActiveExchangeData(data);
    }
  }

  const [aociOpen, setAociOpen] = useState(false);
  function onAociClick() {
    setAociOpen(true);
  }

  const [accDefAmount, setAccDefAmount] = useState(null);
  function onAccumulatedDeficitClick(amt) {
    setAccDefAmount(amt);
  }

  const renderRow = ({name, amount, indent, config, id}) => {
    if (config.hideLine) {
      return null;
    }

    // Check if there are any non-zero amounts in cashflowWorkbookData for this indirect_pli_id
    const hasNonZeroWorkbookData = cashflowWorkbookData?.some(
      (workbookEntry) =>
        workbookEntry.indirect_pli_id === id &&
        Math.abs(workbookEntry.amount).toFixed(2) > 0
    );

    // Hide the row if amount is zero AND there are no non-zero workbook entries
    if (
      new Decimal(amount).todp(0).toNumber() === 0 &&
      !hasNonZeroWorkbookData
    ) {
      return null;
    }

    return (
      <CashflowRow
        name={name}
        id={id}
        amount={amount}
        config={config}
        depth={indent + 1}
        balanceSheetPliIds={balanceSheetPliIds}
        cashflowWorkbookData={cashflowWorkbookData}
        onExchangeRateClick={onExchangeRateClick}
        onAociClick={onAociClick}
        onAccumulatedDeficitClick={onAccumulatedDeficitClick}
      />
    );
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const _filename = `${_.has(currentOrg, 'org_metadata.companyLegalName') ? currentOrg.org_metadata.companyLegalName : currentOrg.orgName} - ISCF Workbook - ${period}.xlsx`;
      await triggerDownload(
        () => api.downloadISCFWorkbook({period}),
        _filename
      );
    } catch (_error) {
      console.error(_error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!statementsEnabled) {
    return null;
  }

  const periodType = periods.reportingPeriodMap[period]?.periodType;
  const unsupported = periodType && periodType !== 'month';

  return (
    <>
      <WorkbookPageHeader title="Cash Flow Workbook">
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          <SinglePeriodSelect
            onPeriodChange={handlePeriodChange}
            selectedPeriod={period}
            clearable={false}
            minCalendarDate={getMinPeriodDate(periods)}
            fiscalYearEnd={orgConfig?.fiscalYearEnd}
            disabled={!periods.reportingPeriods.length || periods.loading}
            placeholder="Data unavailable"
            loading={periods.loading}
            sx={periodSelectStyles}
          />
          <LoadingButton
            onClick={handleDownload}
            loading={isDownloading}
            disabled={!period || isFetching}
            startIcon={<DownloadIcon size={16} />}
            variant="contained"
            color="green"
          >
            Download Workbook
          </LoadingButton>
        </Box>
      </WorkbookPageHeader>
      <Box mt={APP_HEADER_HEIGHT} sx={{flex: 1, overflow: 'hidden'}}>
        <TableContainer mt={APP_HEADER_HEIGHT} sx={{maxHeight: '100%'}}>
          {unsupported && (
            <Box mt={6} height="100%">
              <NoData
                hidden={false}
                reportType="INDIRECT_CASHFLOW"
                periodType={periodType}
              />
            </Box>
          )}
          {isFetching && !unsupported && <LoadingWorkbookTable />}
          {!isFetching && !unsupported && (
            <>
              <Table
                sx={{borderCollapse: 'separate', 'p, span': {fontSize: 12}}}
              >
                <BalanceSheetHead
                  balanceSheetData={balanceSheetSummaryData}
                  differenceData={differenceData}
                />
                <TableBody>
                  {renderRows(reportConfig)}
                  <TotalsRow totals={totalsRow} />
                </TableBody>
              </Table>
              <ExchangeRateModal
                open={Boolean(activeExchangeData)}
                exchangeRateFx={activeExchangeData}
                onClose={() => setActiveExchangeData(null)}
              />
              <AociModal
                open={aociOpen}
                onClose={() => setAociOpen(false)}
                ctaSummaryData={ctaSummaryData}
              />
              <AccumulatedDeficitModal
                open={_.isNumber(accDefAmount)}
                amount={accDefAmount}
                onClose={() => setAccDefAmount(null)}
                ctaSummaryData={ctaSummaryData}
              />
            </>
          )}
        </TableContainer>
      </Box>
    </>
  );
}

export default IndirectCashflowPage;
